import {
  SmsVerificationActionTypes,
  smsVerificationGetBookingVerificationStatusSuccess,
  smsVerificationVerifyBookingChange,
} from '@actions/sms-verification.action';
import { Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Contact, Passenger } from '@models/booking.models';
import { TripDetailsCopy } from '@models/cms/trip-details.model';
import { FeatureFlagsEnum } from '@models/environment.type';
import {
  BookingVerificationStatusTypeEnum,
  BookingVerificationTypeEnum,
  ValidationContact,
} from '@models/sms-validation.model';
import { Store } from '@ngrx/store';
import {selectAppCmsCopy, selectSkybidMMBHomePageV6Copy} from '@selectors/cms.selector';
import { selectSmsVerificationBookingVerified } from '@selectors/sms-verification.selector';
import { FeatureFlagService } from '@services/feature-flag/feature-flag.service';
import { ModalService } from '@services/modal/modal.service';
import { Subject, take } from 'rxjs';
import { TripDetailsValidationSelectModalComponent } from 'src/app/features/trips/trip-details/trip-details-validation-select-modal/trip-details-validation-select-modal.component';
import {GrowthBookService} from "@services/growthbook/growthbook.service";

@Injectable({
  providedIn: 'root',
})
export class SmsValidationService {
  // use this subject to cancel previous authorization subs.
  // call next if not using authorizeChange() and opening
  // SmsVerifyModal
  nextAuthAttempt = new Subject<void>();

  private bookingVerificationStatus = toSignal(
    this.store.select(selectSmsVerificationBookingVerified),
  );
  private appCopy = toSignal(this.store.select(selectAppCmsCopy));
  private skybidV6Copy = toSignal(this.store.select(selectSkybidMMBHomePageV6Copy));

  constructor(
    private featureFlagService: FeatureFlagService,
    private modalService: ModalService,
    private store: Store,
    private gbs: GrowthBookService,
  ) {}

  authorizeChange(
    passengers: { [key: string]: Passenger },
    contacts: { [key: string]: Contact },
    copy: TripDetailsCopy,
  ): void {
    this.nextAuthAttempt.next();

    if (
      !this.featureFlagService.isFeatureActive(
        FeatureFlagsEnum.CHANGE_VALIDATION,
      ) ||
      !!this.bookingVerificationStatus()
    ) {
      this.store.dispatch(
        smsVerificationGetBookingVerificationStatusSuccess({
          status: BookingVerificationStatusTypeEnum.VERIFIED,
        }),
      );
      return;
    }

    const contactList: ValidationContact[] = [];

    Object.keys(passengers).forEach((key) => {
      const pax = passengers[key];
      if (pax?.addresses) {
        contactList.push(
          ...pax.addresses
            .filter(
              (a) => (a.phone?.length ?? 0) >= 7 && a.phone?.startsWith('+'),
            )
            .map(
              (a) =>
                ({
                  key: key,
                  phoneNumber: a.phone,
                  verificationType: BookingVerificationTypeEnum.PASSENGER,
                }) as ValidationContact,
            ),
        );
      }
    });

    Object.keys(contacts).forEach((key) => {
      const contact = contacts[key];
      if (contact?.phoneNumbers) {
        contactList.push(
          ...contact.phoneNumbers
            .filter((p) => p.number.length >= 7 && p.number?.startsWith('+'))
            .map(
              (p) =>
                ({
                  key: key,
                  phoneNumber: p.number,
                  phoneNumberType: p.type,
                  verificationType: BookingVerificationTypeEnum.CONTACT,
                }) as ValidationContact,
            ),
        );
      }
    });

    const isSkybidV6Enabled = this.gbs.isVariantOfExperimentEnabled('feat-003128-skybid-mmb-home-page-revamp-v6', 'b');
    const validationRef = this.modalService.show(
      TripDetailsValidationSelectModalComponent,
      {
        class: 'modal-desktop-600',
        initialState: {
          validationContactList: contactList.filter(
            (contact, i, arr) =>
              arr.findIndex((c) => c.phoneNumber === contact.phoneNumber) === i,
          ),
          tripDetailsCopy: copy,
          appCopy: this.appCopy() ?? undefined,
          isSkybidV6enabled: isSkybidV6Enabled,
          skybidV6Copy: this.skybidV6Copy() ?? undefined
        },
      },
    );

    validationRef.content?.selectedContact
      .pipe(take(1))
      .subscribe((value: ValidationContact) => {
        this.store.dispatch(
          smsVerificationVerifyBookingChange({
            request: {
              phoneNumberType: value.phoneNumberType,
              requesterKey: value.key,
              verificationType: value.verificationType,
            },
          }),
        );
      });
  }
}
